import { BottomSheetDrawer } from "@repo/ui/components/bottom-sheet-drawer";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@repo/ui/components/ui/form";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/ui/select";
import { IconChevronDown, IconCircle, IconLoader2 } from "@tabler/icons-react";
import { CircleX } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ComponentPropsWithoutRef, ReactNode, Ref } from "react";
import { useState } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import X from "@/assets/icons/x.svg";
import { RadioSelectItem } from "@/components/radio-select";
import { Button } from "@/components/ui/button";
import { useMobile } from "@/lib/hooks/use-mobile";
import { cn } from "@/lib/utils";

const layouts = {
  default: {
    formItemClasses: "col-span-3 grid grid-cols-3 items-center gap-4 space-y-0",
    selectTriggerClasses: "col-span-2 w-full font-medium",
    formMessageClasses: "col-span-3",
  },
  medium: {
    formItemClasses: "",
    selectTriggerClasses: "h-14 w-full text-base font-medium",
    formMessageClasses: "",
  },
  big: {
    formItemClasses: "",
    selectTriggerClasses: "h-14 md:h-16 w-full text-base font-medium",
    formMessageClasses: "",
  },
};

type InputProps<T extends FieldValues> = {
  form: UseFormReturn<T, unknown, T>;
  fieldName: Path<T>;
  fieldLabel?: string;
  fieldPlaceholder?: string;
  fieldOptions: { label: string | ReactNode; value: string }[];
  required?: boolean;
  layout?: keyof typeof layouts;
  onChange?: () => void;
  clearable?: boolean;
  className?: string;
  classNameTrigger?: string;
  classNameContent?: string;
  classNameItem?: string;
  alwaysDropdown?: boolean;
  floating?: boolean;
  disabled?: boolean;
  loading?: boolean;
  dataPw?: string;
};

type MobileRadioOptionProps = {
  label: string | ReactNode;
  value: string;
  selectedValue: string;
  onSelect: (value: string) => void;
  className?: string;
};

export const MobileRadioOption = ({
  label,
  value,
  selectedValue,
  onSelect,
  className,
}: MobileRadioOptionProps) => {
  const isSelected = value === selectedValue;

  return (
    <button
      aria-pressed={isSelected}
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 p-4 text-left transition-colors",
        "hover:bg-brand-green-300/10",
        isSelected && "font-medium",
        className
      )}
      onClick={() => onSelect(value)}
      tabIndex={0}
      type="button"
    >
      <span
        className={cn(
          "flex size-5 shrink-0 items-center justify-center rounded-full border",
          "bg-brand-white",
          isSelected && "border-brand-green-300 bg-brand-green-300"
        )}
      >
        {isSelected && (
          <IconCircle
            className="h-3 w-3 fill-current text-brand-white"
            stroke={1.25}
          />
        )}
      </span>
      <span className="flex-1">{label}</span>
    </button>
  );
};

type ClearButtonProps = {
  onClear: () => void;
};

const ClearButton = ({ onClear }: ClearButtonProps) => (
  <Button
    className="-translate-y-1/2 absolute top-[50%] right-0.5 m-0 bg-white p-2 hover:bg-white"
    onClick={onClear}
    size="icon"
    type="button"
    variant="ghost"
  >
    <X className="h-4 w-4" />
  </Button>
);

type MobileSelectTriggerProps = {
  disabled: boolean;
  loading: boolean;
  floating: boolean;
  fieldPlaceholder?: string;
  fieldValue: string;
  selectedLabel: ReactNode;
  drawerOpen: boolean;
  triggerClassName: string;
  ref?: Ref<HTMLButtonElement>;
} & ComponentPropsWithoutRef<"button">;

export const MobileSelectTrigger = ({
  disabled,
  loading,
  floating,
  fieldPlaceholder,
  fieldValue,
  selectedLabel,
  drawerOpen,
  triggerClassName,
  ref,
  ...props
}: MobileSelectTriggerProps) => (
  <button
    aria-expanded={drawerOpen}
    aria-haspopup="listbox"
    className={cn(
      "flex h-14 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background",
      "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
      "disabled:cursor-not-allowed disabled:opacity-50",
      triggerClassName
    )}
    disabled={disabled || loading}
    ref={ref}
    tabIndex={0}
    type="button"
    {...props}
  >
    {floating ? (
      <p className="flex flex-col items-start text-base">
        <span
          className={cn(
            "font-normal text-base text-gray-500",
            fieldValue !== "" && !disabled && "text-xs"
          )}
        >
          {fieldPlaceholder}
        </span>
        {fieldValue && <span className="font-medium">{selectedLabel}</span>}
      </p>
    ) : (
      <span className={cn(!fieldValue && "text-muted-foreground")}>
        {fieldValue ? selectedLabel : fieldPlaceholder}
      </span>
    )}
    {loading ? (
      <IconLoader2 className="h-4 w-4 animate-spin" />
    ) : (
      <IconChevronDown className="h-6 w-6" stroke={1.25} />
    )}
  </button>
);

export const FormSelect = <T extends FieldValues>({
  form,
  dataPw,
  fieldName,
  fieldLabel,
  fieldPlaceholder,
  fieldOptions,
  required = false,
  layout = "default",
  onChange,
  clearable = true,
  className,
  classNameTrigger,
  classNameContent,
  classNameItem,
  alwaysDropdown = false,
  floating = false,
  disabled = false,
  loading = false,
}: InputProps<T>) => {
  const t = useTranslations();
  const { isMobile } = useMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const getSelectedLabel = (value: string): ReactNode => {
    const option = fieldOptions.find((opt) => opt.value === value);
    return option?.label ?? "";
  };

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field, fieldState }) => (
        <FormItem
          className={cn("relative", layouts[layout].formItemClasses, className)}
        >
          {fieldLabel && (
            <FormLabel className={cn("space-x-2 font-semibold")}>
              <span>{fieldLabel}</span>
              {required && <span>*</span>}
            </FormLabel>
          )}

          {isMobile && !alwaysDropdown ? (
            <>
              <BottomSheetDrawer
                content={
                  <div className="divide-y">
                    {fieldOptions.map((option) => (
                      <MobileRadioOption
                        key={option.value}
                        label={option.label}
                        onSelect={(value) => {
                          if (field.value !== value) {
                            field.onChange(value);
                            form.trigger(fieldName);
                          }
                          onChange?.();
                          setDrawerOpen(false);
                        }}
                        selectedValue={field.value}
                        value={option.value}
                      />
                    ))}
                  </div>
                }
                onOpenChange={setDrawerOpen}
                open={drawerOpen}
                title={fieldPlaceholder ?? fieldLabel}
                trigger={
                  <MobileSelectTrigger
                    data-pw={dataPw}
                    disabled={disabled}
                    drawerOpen={drawerOpen}
                    fieldPlaceholder={fieldPlaceholder}
                    fieldValue={field.value}
                    floating={floating}
                    loading={loading}
                    selectedLabel={getSelectedLabel(field.value)}
                    triggerClassName={cn(
                      layouts[layout].selectTriggerClasses,
                      fieldState.error && "border-brand-red-300",
                      classNameTrigger
                    )}
                  />
                }
              />
              {field.value && clearable && (
                <ClearButton
                  onClear={() => {
                    field.onChange("");
                    onChange?.();
                  }}
                />
              )}
            </>
          ) : (
            <Select
              disabled={disabled || loading}
              onValueChange={(value: string) => {
                if (field.value !== value) {
                  field.onChange(value);
                  form.trigger(fieldName);
                }
                onChange?.();
              }}
              value={field.value}
            >
              <FormControl className="m-0">
                <SelectTrigger
                  className={cn(
                    "h-14",
                    layouts[layout].selectTriggerClasses,
                    classNameTrigger,
                    fieldState.error && "border-brand-red-300"
                  )}
                  data-pw={dataPw}
                >
                  {floating ? (
                    <p className="flex flex-col items-start text-base">
                      <span
                        className={cn(
                          "font-normal text-base text-gray-500",
                          field.value !== "" && !disabled && "text-xs"
                        )}
                      >
                        {fieldPlaceholder}
                      </span>
                      <SelectValue />
                    </p>
                  ) : (
                    <SelectValue placeholder={fieldPlaceholder} />
                  )}
                  {loading && (
                    <IconLoader2 className="-translate-y-1/2 absolute top-1/2 right-4 h-4 w-4 animate-spin bg-white" />
                  )}
                </SelectTrigger>
              </FormControl>
              <SelectContent className={classNameContent} id={fieldName}>
                {fieldOptions.map((option) => (
                  <RadioSelectItem
                    className={classNameItem}
                    key={option.value}
                    label={option.label}
                    optionValue={option.value}
                    value={field.value}
                  />
                ))}
              </SelectContent>
              {field.value && clearable && (
                <ClearButton
                  onClear={() => {
                    field.onChange("");
                    onChange?.();
                  }}
                />
              )}
            </Select>
          )}
          {fieldState.error?.message && (
            <div
              className={cn(
                "flex items-start gap-1",
                layouts[layout].formMessageClasses
              )}
            >
              <CircleX
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-text-danger"
              />
              <p
                aria-live="assertive"
                className="text-sm text-text-danger leading-5.5"
                role="alert"
              >
                {t(fieldState.error.message)}
              </p>
            </div>
          )}
        </FormItem>
      )}
    />
  );
};
